import { TriangleAlert } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button } from "./Button";
import { Dialog } from "./Dialog";
import { Input } from "./Field";

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for deletes and anything else that can't be undone. */
  variant?: "danger" | "primary";
  /**
   * Requires the user to type this exact phrase before confirming.
   *
   * Reserve it for actions that are both irreversible and wide — deleting a
   * hundred selected products, not one row. Asking for it routinely trains
   * people to type past it, which costs the guard its whole value.
   */
  requireText?: string;
  /** Extra consequence detail, listed under the warning. */
  consequences?: string[];
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/**
 * Promise-based confirmation, replacing the ad-hoc `ConfirmDialog` call sites
 * scattered through the Vaadin views:
 *
 *   if (await confirm({ title: t("dialog.delete.title"), variant: "danger" })) {
 *     await remove(ids);
 *   }
 *
 * Every destructive action in the suite goes through this, so the wording and
 * button styling stay consistent without each screen re-deciding them.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [typed, setTyped] = useState("");
  const resolver = useRef<((confirmed: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>((next) => {
    setOptions(next);
    setTyped("");
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((confirmed: boolean) => {
    resolver.current?.(confirmed);
    resolver.current = null;
    setOptions(null);
    setTyped("");
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  const danger = options?.variant === "danger";
  const phrase = options?.requireText;
  // Exact match, including case: the phrase is deliberately shouty so it has to
  // be read and copied rather than pattern-matched.
  const unlocked = !phrase || typed === phrase;

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog
        open={options !== null}
        onClose={() => settle(false)}
        title={options?.title ?? ""}
        {...(options?.message && !phrase ? { description: options.message } : {})}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {options?.cancelLabel ?? "Cancel"}
            </Button>
            <Button
              variant={danger ? "danger" : "primary"}
              onClick={() => settle(true)}
              disabled={!unlocked}
              autoFocus={!phrase}
            >
              {options?.confirmLabel ?? "Confirm"}
            </Button>
          </>
        }
      >
        {(phrase || options?.consequences?.length) && (
          <div className="flex flex-col gap-3">
            <div
              className={cnWarning(danger)}
              role="alert"
            >
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              <div className="min-w-0">
                {options?.message && <p>{options.message}</p>}
                {options?.consequences && options.consequences.length > 0 && (
                  <ul className="mt-1.5 list-disc pl-4">
                    {options.consequences.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {phrase && (
              <label className="flex flex-col gap-1.5">
                <span className="text-[13px] text-ink-muted">
                  Type <span className="font-mono font-semibold text-ink">{phrase}</span> to confirm
                </span>
                <Input
                  value={typed}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => setTyped(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && unlocked) settle(true);
                  }}
                />
              </label>
            )}
          </div>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

function cnWarning(danger: boolean): string {
  return [
    "flex items-start gap-2.5 rounded-md px-3 py-2.5 text-[13px]",
    danger ? "bg-risk-soft text-risk" : "bg-warn-soft text-warn",
  ].join(" ");
}

export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error("useConfirm must be used inside a ConfirmProvider");
  }
  return confirm;
}
